// === THIRD-PARTY ===
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// === STYLE / MATERIAL UI ===
import { useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import { Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Button
} from '@mui/material';

// === COMPONENTS ===
import { AuditQCPanel } from './AuditQCPanel';
import { AddAuditQCDialog } from './AddAuditQCRowDialog/AddAuditQCDialog';

// === HOOKS ===
import { useSession } from 'pages/PageManager/components/SessionContext';

//-----------------------------------------------------------------
export function AuditQC() {
  //-----------------------------------------------------------------

  //#region [VALUES]
  
  const { user_details } = useSession();

  const theme        = useTheme();
  const navigate     = useNavigate();
  const has_access   = user_details.roles?.some((role) => role.RoleName === 'auditQC');
  const page         = localStorage.getItem('display_page') || 'verify';

  const [ is_open_add_row, setShowAddRowsModal ] = useState( false );

  //#endregion
  
  //#region [STYLES]
  
const _styles = {
  container: {
    height    : `calc(100% - 5rem)`,
    'z-index' : '0',
    padding   : '1rem',
    backgroundColor : theme.palette.background.secondary[theme.palette.mode],
    overflow: 'auto',
  },
    control_container: {
      borderRadius: '1rem',
      display: 'flex',
      justifyContent: 'flex-end',
      width: '100%',
      padding: '0.5rem',
      marginBottom: '1rem',
      backgroundColor: theme.palette.background.primary[theme.palette.mode],
    },   
    button_add_rows: {
      margin: '1rem',
      backgroundColor: theme.palette.success.main,
      '&:hover': {
        backgroundColor: theme.palette.success.dark,
      },
    },   
    audit_panel: {
      width: '100%',
      backgroundColor : theme.palette.background.primary[theme.palette.mode],
    },
    audit_detail: {
      height: '60%',
      padding: 0,
    }  
  }

  // === ATTRIBUTES
  const CONTAINER = {
    sx : _styles.container,
  }
  const DIALOG_ADD_ROW = {
    showDialog    : is_open_add_row    ,
    setShowDialog : setShowAddRowsModal ,   
  } 
  const CONTAINER_CONTROLS = {
    sx : _styles.control_container,
  }
  const BUTTON_ADD_ROWS = {
    sx        : _styles.button_add_rows,
    variant   : 'contained',
    startIcon : <LibraryAddIcon />,
    onClick   : () => setShowAddRowsModal(true),      
    children  : [ 'Add Row' ],
    name      : 'add_audit_qc_row_btn',
  }
  const AUDIT_PANEL = {
    sx              : _styles.audit_panel,
    key             : 'audit-panel',
    variant         : 'primary',
    defaultExpanded : true,
  }
  const AUDIT_HEADER = {
    variant : 'h5',
    color   : 'primary',
    children: ['Audit'],
  }
  const AUDIT_DETAIL = {
    sx  : _styles.audit_detail,
    key : 'audit-detail',
  }

  //#endregion

  //#region [EFFECT]

  useEffect(()=>{
    if( user_details.roles.length === 0 ) return;
    if( !has_access ) {
      navigate( '/' + page );    
    }
  }, [user_details, has_access, page, navigate]);

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    if( !has_access ) return null;
    return (
      <Box                  { ...CONTAINER            }>
        <AddAuditQCDialog   { ...DIALOG_ADD_ROW       }/>   
        <Box                { ...CONTAINER_CONTROLS   }>       
          <Button           { ...BUTTON_ADD_ROWS      }/>
        </Box>     
        <Accordion          { ...AUDIT_PANEL          }>
          <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
            <Typography     { ...AUDIT_HEADER         }/>
          </AccordionSummary>
          <AccordionDetails { ...AUDIT_DETAIL         }>
            <AuditQCPanel />
          </AccordionDetails>
        </Accordion>
    </Box>
    );
  };

  //#endregion

  return display();
}
